
import React from 'react'
import { Grid } from '@mui/material'
import { withStyles, createStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { compose } from 'widgets-shared/core'
import MarketLine from '../../components/MarketLine/MarketLine'
import MainLinesHeader from './MainLinesHeader/MainLinesHeader'

import { MarketSelectionType } from '../../types/market'
import { PropsComponent, Record } from './types'
import { CompetitorWithFixtureData, Competitor } from 'smartstream-widgets/types/competitor'
import { homeFirstCompetitions } from "smartstream-widgets/constants/shared"
import CTAOffer from 'smartstream-widgets/components/CTAOffer/CTAOffer'
import { getDefaultTheme } from 'smartstream-widgets/utils/getDefaultTheme'
import { withComponentContextProvider } from 'widgets-shared/core'

type CompetitorWithMarketSelection = {
  competitor?: CompetitorWithFixtureData,
  selection?: MarketSelectionType
}

type CompetitorsWithMarketSelection = {
  AWAY?: CompetitorWithMarketSelection,
  HOME?: CompetitorWithMarketSelection
}

const styles = (theme: Theme) => createStyles({
  linesContainer: {
    flexGrow: 1,
    paddingBottom: 5,
    width: 'fit-content',
    alignSelf: 'center',
    justifyContent: 'space-evenly',
    flexDirection: 'column'
  },
  linesContainerFewMarkets: {
  },
  marketOddLines: {
    padding: `0px ${theme.spacing(2)}`,
    justifyContent: 'center'
  },
  mainlinesOddButton: {
    minHeight: 30,
    [theme.breakpoints.up(300)]: {
      minWidth: 72
    },
    [theme.breakpoints.up(768)]: {
      minWidth: 120
    },
    [theme.breakpoints.up(900)]: {
      minWidth: 140
    },
    [theme.breakpoints.up(1440)]: {
      minWidth: 200
    }
  },
  mainlinesMarketLabel: {
    width: 124,
    fontSize: 12,
    [theme.breakpoints.up(420)]: {      
      width: 195
    },
    [theme.breakpoints.up(900)]: {
      width: 327
    },
    [theme.breakpoints.up(1440)]: {
      fontSize: 16,
      width: 415
    }
  },
  root: {
    height: '100%',
    paddingBottom: 35
  },
  ctaOffer: {
    height: 17
  },
  rootDrawMainlines: {
    gridTemplateColumns: '0px 1fr',
  },
  captionOdd:{
    fontSize: 12,
    '&:nth-last-child(3)': {
      fontSize: 10,
      fontWeight: 'normal'
    }
  },  
})

export function findCompetitorsWithRecords(competitors: Array<Competitor>, records: Array<Record>): CompetitorsWithMarketSelection {
  return competitors.reduce((result, competitor, index) => {
    const competitorRecords = records.find((record: Record) => {
      return record.competitorId === competitor.competitorId
    })
    const homeOrAway = competitor.homeOrAway || (index ? 'AWAY' : 'HOME')
    result[homeOrAway] = {
      ...competitor,
      records: competitorRecords || {} as Record
    }
    return result
  }, {})
}

export function findCompetitorsWithSelection(
  competitors: Array<Competitor>,
  marketType: string,
  selections: Array<MarketSelectionType>
): CompetitorsWithMarketSelection {
  if (marketType === 'overUnder') {
    return {
      AWAY: {
        selection: selections.find(selection => selection.outcome === 'Under')
      },
      HOME: {
        selection: selections.find(selection => selection.outcome === 'Over')
      }
    }
  }

  return competitors.reduce((result, competitor, index) => {
    const { competitorId, officialName } = competitor
    const competitorSelection = selections.find(selection => selection.competitorId === competitorId || selection.name === officialName)
    const homeOrAway = competitor.homeOrAway || (index ? 'AWAY' : 'HOME')
    result[homeOrAway] = { selection: competitorSelection }
    return result
  }, {})
}

export function MainLinesComponent(props: PropsComponent) {
  const {
    ctaOfferIsDisabledOnWidget,
    hideCtaOfferOnWidget,
    classes,
    className = '',
    configOptions: {
      showRecords,
      bookmakers
    } = {},
    isBookmakerLinkEnabled,
    sourceId,
    theme,
    data: {
      competitionId,
      competitors,
      fixtureId,
      markets,
      matchSummaries,
      records,
      scheduledStartTime,
      venueName
    } = {} } = props
  const isAwayTeamFirst = !homeFirstCompetitions.includes(competitionId)
  const competitorsWithRecords = competitors && records && findCompetitorsWithRecords(competitors, records)
  const amountOfMarkets = markets && Object.values(markets).filter(market => market).length
  const linesContainerClass = amountOfMarkets < 3
    ? `${classes.linesContainer} ${classes.linesContainerFewMarkets}` : classes.linesContainer
  const matchState = matchSummaries ?
    {
      scoreAway: matchSummaries.awayScore,
      scoreHome: matchSummaries.homeScore,
      phase: matchSummaries.phase,
      matchStatus: matchSummaries.matchStatus
    } : {}

  const bookMakerSource = bookmakers && bookmakers[sourceId] && bookmakers[sourceId]
  const ctaOffer = bookMakerSource && bookMakerSource.ctaOffer
  const ctaOfferIsEnabled = bookMakerSource && bookMakerSource.ctaOfferIsEnabled
  return (
    <>
      {
        competitors && records &&
        <Grid
          container
          direction='column'
          wrap='nowrap'
          justifyContent='space-between'
          className={`${classes.root} ${className}`}
          data-testid='main-lines-component-container'
        >
          <MainLinesHeader
            competitors={competitorsWithRecords}
            isAwayTeamFirst={isAwayTeamFirst}
            scheduledStartTime={scheduledStartTime}
            venueName={venueName}
            matchState={matchState}
            records={records}
            showRecords={showRecords}
          />
          {ctaOffer && ctaOfferIsEnabled &&
            <CTAOffer
              classes={{ root: classes.ctaOffer }}
              offerText={ctaOffer.offerText}
              link={ctaOffer.link}
              linkText={ctaOffer.linkText}
              dismissibleOffer={ctaOffer.dismissibleOffer}
              hideCtaOfferOnWidget={hideCtaOfferOnWidget}
              ctaOfferIsDisabledOnWidget={ctaOfferIsDisabledOnWidget}
              theme={theme}
            />
          }
          <Grid
            alignContent={amountOfMarkets < 3 ? 'center' : 'stretch'}
            container
            justifyContent='space-between'
            className={linesContainerClass}
          >
            {markets && Object.keys(markets).map((marketKey, key) => {
              const market = markets[marketKey] && markets[marketKey][0]

              if (market) {
                const competitorsWithSelections =
                  findCompetitorsWithSelection(competitors, marketKey, market && market.selections)

                if (competitorsWithSelections.HOME.selection && competitorsWithSelections.AWAY.selection) {
                  return (
                    <MarketLine
                      animationKey={fixtureId}
                      key={key}
                      classes={{
                        root: classes.marketOddLines,
                        oddButton: classes.mainlinesOddButton,
                        marketLabel: classes.mainlinesMarketLabel,
                        rootDraw: classes.rootDrawMainlines,
                        captionOdd: classes.captionOdd
                      }}
                      isBookmakerLinkEnabled={isBookmakerLinkEnabled}
                      competitors={competitors}
                      competitionId={competitionId}
                      marketTypeId={market.marketTypeId}
                      selections={market.selections}
                      isAwayTeamFirst={isAwayTeamFirst}
                      labelSet='european'
                    />
                  )
                }
              }
            })}
          </Grid>
        </Grid>
      }
    </>
  )
}

export const MainLinesComponentComposed = compose(withStyles(styles, { name: 'MainLinesComponent', defaultTheme: getDefaultTheme }))(MainLinesComponent) as React.ElementType

export default MainLinesComponentComposed

export const MainLinesComponentComposedtWithContext = withComponentContextProvider(MainLinesComponentComposed) as React.Component